<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useCurrency } from 'dashboard/composables/useCurrency';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversation: {
    type: Object,
    default: () => ({}),
  },
  currentValue: {
    type: Number,
    default: 0,
  },
  // Controlado pelo pai (que faz a chamada à API): mantém o botão em loading
  // enquanto o request roda. O ref local resetava síncrono e nunca aparecia.
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'confirm']);

const store = useStore();
const { t } = useI18n();
const { currencySymbol } = useCurrency();

const finalValue = ref(props.currentValue || '');
const closeDate = ref(new Date().toISOString().split('T')[0]);
const notes = ref('');
const selectedWonReason = ref('');

// Won reasons from store (optional field)
const wonReasons = computed(
  () => store.getters['wonReasons/getActiveWonReasons'] || []
);

onMounted(() => {
  store.dispatch('wonReasons/get', true); // true = active only
});

// Watch for prop changes
watch(
  () => props.currentValue,
  newValue => {
    finalValue.value = newValue || '';
  }
);

watch(
  () => props.show,
  newValue => {
    if (newValue) {
      // Reset form when modal opens
      finalValue.value = props.currentValue || '';
      closeDate.value = new Date().toISOString().split('T')[0];
      notes.value = '';
      selectedWonReason.value = '';
    }
  }
);

const contactName = computed(() => {
  return (
    props.conversation?.contact?.name ||
    props.conversation?.meta?.sender?.name ||
    'Cliente'
  );
});

const handleClose = () => {
  emit('close');
};

const handleConfirm = () => {
  if (props.isSubmitting) return;

  const wonReason = wonReasons.value.find(
    r => String(r.id) === String(selectedWonReason.value)
  );
  emit('confirm', {
    conversationId: props.conversation.id,
    finalValue:
      finalValue.value === '' || finalValue.value === null
        ? null
        : parseFloat(finalValue.value),
    closeDate: closeDate.value,
    notes: notes.value,
    wonReasonId: selectedWonReason.value || null,
    wonReason: wonReason?.name || null,
  });
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      @click.self="handleClose"
    >
      <div
        class="win-modal w-full max-w-md overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-2xl"
      >
        <!-- Header -->
        <div class="flex items-center justify-between p-5">
          <div class="flex items-center gap-2.5">
            <span
              class="flex h-9 w-9 items-center justify-center rounded-lg bg-n-teal-3 text-n-teal-11"
            >
              <span class="i-lucide-trophy text-lg" />
            </span>
            <div class="min-w-0">
              <h3 class="text-base font-semibold text-n-slate-12">
                {{ t('KANBAN.DEAL.WIN.TITLE') }}
              </h3>
              <p class="truncate text-sm text-n-slate-10">{{ contactName }}</p>
            </div>
          </div>
          <button
            type="button"
            class="flex h-7 w-7 items-center justify-center rounded-md text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="handleClose"
          >
            <span class="i-lucide-x text-base" />
          </button>
        </div>

        <!-- Body -->
        <div class="space-y-4 px-5 pb-2">
          <!-- Celebration message -->
          <div
            class="flex items-start gap-2.5 rounded-xl border border-n-teal-5 bg-n-teal-3 p-3"
          >
            <span
              class="i-lucide-party-popper mt-0.5 shrink-0 text-base text-n-teal-11"
            />
            <p class="text-sm text-n-teal-12">
              {{ t('KANBAN.DEAL.WIN.CELEBRATION') }}
            </p>
          </div>

          <!-- Final Value -->
          <div>
            <label class="mb-1.5 block text-sm font-medium text-n-slate-11">
              {{ t('KANBAN.DEAL.WIN.FINAL_VALUE') }}
              <span class="font-normal text-n-slate-10">
                {{ t('KANBAN.DEAL.OPTIONAL_LABEL') }}
              </span>
            </label>
            <div
              class="flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-1 px-3 transition focus-within:border-n-green-9"
            >
              <span class="shrink-0 text-base font-semibold text-n-slate-10">
                {{ currencySymbol }}
              </span>
              <input
                v-model.number="finalValue"
                type="number"
                step="0.01"
                min="0"
                class="w-full border-0 bg-transparent py-2.5 text-base font-semibold text-n-slate-12 shadow-none outline-none focus:border-0 focus:shadow-none focus:outline-none focus:ring-0 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
            </div>
            <p class="mt-1.5 text-xs text-n-slate-10">
              {{ t('KANBAN.DEAL.WIN.VALUE_HINT') }}
            </p>
          </div>

          <!-- Close Date -->
          <div>
            <label class="mb-1.5 block text-sm font-medium text-n-slate-11">
              {{ t('KANBAN.DEAL.WIN.CLOSE_DATE') }}
            </label>
            <input
              v-model="closeDate"
              type="date"
              class="w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5 text-sm text-n-slate-12 focus:border-n-green-9 focus:outline-none"
            />
          </div>

          <!-- Won Reason (optional) -->
          <div v-if="wonReasons.length > 0">
            <label class="mb-1.5 block text-sm font-medium text-n-slate-11">
              {{ t('KANBAN.DEAL.WIN.WON_REASON') }}
              <span class="font-normal text-n-slate-10">
                {{ t('KANBAN.DEAL.OPTIONAL_LABEL') }}
              </span>
            </label>
            <div class="relative w-full">
              <select
                v-model="selectedWonReason"
                class="w-full appearance-none bg-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5 text-sm text-n-slate-12 ltr:pr-9 rtl:pl-9 focus:border-n-green-9 focus:outline-none"
              >
                <option value="">
                  {{ t('KANBAN.DEAL.WIN.WON_REASON_PLACEHOLDER') }}
                </option>
                <option
                  v-for="reason in wonReasons"
                  :key="reason.id"
                  :value="reason.id"
                >
                  {{ reason.name }}
                </option>
              </select>
              <span
                class="absolute top-1/2 -translate-y-1/2 pointer-events-none i-lucide-chevron-down size-4 text-n-slate-10 ltr:right-3 rtl:left-3"
              />
            </div>
          </div>

          <!-- Notes (optional) -->
          <div>
            <label class="mb-1.5 block text-sm font-medium text-n-slate-11">
              {{ t('KANBAN.DEAL.WIN.NOTES') }}
              <span class="font-normal text-n-slate-10">
                {{ t('KANBAN.DEAL.OPTIONAL_LABEL') }}
              </span>
            </label>
            <textarea
              v-model="notes"
              rows="3"
              :placeholder="t('KANBAN.DEAL.WIN.NOTES_PLACEHOLDER')"
              class="w-full resize-none rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-n-green-9 focus:outline-none"
            />
          </div>
        </div>

        <!-- Footer -->
        <div class="mt-2 flex items-center justify-end gap-2 p-5">
          <Button variant="faded" color="slate" @click="handleClose">
            {{ t('KANBAN.DEAL.CANCEL') }}
          </Button>
          <Button
            color="green"
            class="whitespace-nowrap"
            :is-loading="isSubmitting"
            :disabled="isSubmitting"
            @click="handleConfirm"
          >
            <span class="i-lucide-trophy mr-1" />
            {{ t('KANBAN.DEAL.WIN.CONFIRM') }}
          </Button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
